import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { LogOut, Plus, Pencil, Trash2, Eye, Inbox, Settings, Video, Upload, Loader2, FileText } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FileUploader({ accept, label, token, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setProgress(`Uploading ${file.name}...`);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/upload-file`, formData, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      onUploaded(res.data.url);
      const sizeMB = (res.data.size / (1024 * 1024)).toFixed(1);
      toast.success(`Uploaded! (${sizeMB} MB)`);
      setProgress("");
    } catch (err) {
      toast.error("Upload failed");
      setProgress("");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="mt-2">
      <input
        ref={fileRef}
        type="file"
        accept={accept}
        onChange={handleFile}
        disabled={uploading}
        className="hidden"
        id={`upload-${label}`}
      />
      <label
        htmlFor={`upload-${label}`}
        className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] font-bold px-3 py-1.5 border border-[#27272A] text-[#A1A1AA] rounded-none cursor-pointer hover:border-[#52525B] hover:text-white transition-all ${uploading ? "opacity-50 pointer-events-none" : ""}`}
      >
        {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
        {uploading ? "Uploading..." : label}
      </label>
      {progress && <p className="text-[10px] text-[#38BDF8] mt-1">{progress}</p>}
    </div>
  );
}

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.success) {
        onLogin(res.data.token);
      }
    } catch {
      setError("Invalid password");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <h1 className="font-['Syne'] text-3xl font-bold text-white mb-2">Admin</h1>
        <p className="text-sm text-[#A1A1AA] mb-8">Enter the admin password to continue.</p>
        <form onSubmit={handleLogin} className="space-y-4">
          <Input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] rounded-sm"
          />
          {error && <p className="text-xs text-red-400">{error}</p>}
          <Button
            type="submit"
            data-testid="admin-login-btn"
            className="w-full bg-white text-[#050505] hover:bg-[#38BDF8] font-bold uppercase tracking-wider text-sm rounded-sm"
          >
            Login
          </Button>
        </form>
      </motion.div>
    </div>
  );
}

function ProjectForm({ project, onSave, onCancel, token }) {
  const [form, setForm] = useState(
    project || {
      title: "",
      genre: "",
      synopsis: "",
      poster_image: "",
      teaser_video: "",
      cast_crew: "",
      status: "In Development",
      release_date: "",
      featured: false,
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) {
      toast.error("Title is required");
      return;
    }
    try {
      const headers = { "X-Admin-Token": token };
      if (project) {
        await axios.put(`${API}/admin/projects/${project.id}`, form, { headers });
        toast.success("Project updated!");
      } else {
        await axios.post(`${API}/admin/projects`, form, { headers });
        toast.success("Project created!");
      }
      onSave();
    } catch (err) {
      toast.error("Failed to save project");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/upload-file`, formData, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      setForm({ ...form, poster_image: res.data.url });
      toast.success("Image uploaded!");
    } catch {
      toast.error("Upload failed");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#A1A1AA] mb-2 block">Title *</Label>
          <Input
            data-testid="project-title-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Project title"
            className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] rounded-sm"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#A1A1AA] mb-2 block">Genre</Label>
          <Input
            data-testid="project-genre-input"
            value={form.genre}
            onChange={(e) => setForm({ ...form, genre: e.target.value })}
            placeholder="e.g. Drama, Horror, Thriller"
            className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] rounded-sm"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#A1A1AA] mb-2 block">Synopsis</Label>
        <Textarea
          data-testid="project-synopsis-input"
          value={form.synopsis}
          onChange={(e) => setForm({ ...form, synopsis: e.target.value })}
          placeholder="Brief description of the project..."
          rows={3}
          className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] rounded-sm resize-none"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#A1A1AA] mb-2 block">Poster Image</Label>
          <Input
            data-testid="project-poster-input"
            value={form.poster_image}
            onChange={(e) => setForm({ ...form, poster_image: e.target.value })}
            placeholder="URL or upload a file"
            className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] rounded-sm"
          />
          <FileUploader
            accept="image/*"
            label="Upload Image"
            token={token}
            onUploaded={(url) => setForm({ ...form, poster_image: url })}
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#A1A1AA] mb-2 block">Teaser Video</Label>
          <Input
            data-testid="project-video-input"
            value={form.teaser_video}
            onChange={(e) => setForm({ ...form, teaser_video: e.target.value })}
            placeholder="YouTube/Vimeo link or upload"
            className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] rounded-sm"
          />
          <FileUploader
            accept="video/*"
            label="Upload Video"
            token={token}
            onUploaded={(url) => setForm({ ...form, teaser_video: url })}
          />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#A1A1AA] mb-2 block">Cast & Crew</Label>
        <Input
          data-testid="project-cast-input"
          value={form.cast_crew}
          onChange={(e) => setForm({ ...form, cast_crew: e.target.value })}
          placeholder="Director: ..., Stars: ..."
          className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] rounded-sm"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#A1A1AA] mb-2 block">Status</Label>
          <Select
            value={form.status}
            onValueChange={(v) => setForm({ ...form, status: v })}
          >
            <SelectTrigger data-testid="project-status-select" className="bg-[#0A0A0A] border-[#27272A] text-white rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-[#27272A]">
              <SelectItem value="In Development">In Development</SelectItem>
              <SelectItem value="In Production">In Production</SelectItem>
              <SelectItem value="Released">Released</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#A1A1AA] mb-2 block">Release Date</Label>
          <Input
            data-testid="project-release-input"
            value={form.release_date}
            onChange={(e) => setForm({ ...form, release_date: e.target.value })}
            placeholder="e.g. 2026"
            className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] rounded-sm"
          />
        </div>
        <div className="flex items-end pb-1">
          <div className="flex items-center gap-3">
            <Switch
              data-testid="project-featured-switch"
              checked={form.featured}
              onCheckedChange={(c) => setForm({ ...form, featured: c })}
            />
            <Label className="text-sm text-[#A1A1AA]">Featured</Label>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          type="submit"
          data-testid="project-save-btn"
          className="bg-white text-[#050505] hover:bg-[#38BDF8] font-bold uppercase tracking-wider text-sm rounded-sm"
        >
          {project ? "Update Project" : "Create Project"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          data-testid="project-cancel-btn"
          className="border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#52525B] rounded-sm"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

function SiteSettings({ token }) {
  const [settings, setSettings] = useState({
    hero_video_url: "",
    hero_image_url: "",
    hero_title: "",
    hero_subtitle: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings({
          hero_video_url: res.data.hero_video_url || "",
          hero_image_url: res.data.hero_image_url || "",
          hero_title: res.data.hero_title || "",
          hero_subtitle: res.data.hero_subtitle || "",
        });
      } catch {
        console.error("Failed to fetch settings");
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/settings`, settings, {
        headers: { "X-Admin-Token": token },
      });
      setSettings({
        hero_video_url: res.data.hero_video_url || "",
        hero_image_url: res.data.hero_image_url || "",
        hero_title: res.data.hero_title || "",
        hero_subtitle: res.data.hero_subtitle || "",
      });
      toast.success("Settings saved!");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-none p-6 space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <Video size={16} className="text-[#38BDF8]" />
        <h2 className="font-['Syne'] text-lg font-bold text-white">Hero Section</h2>
      </div>
      <p className="text-xs text-[#52525B]">Configure the homepage hero background video and fallback image. The video plays on loop automatically.</p>

      <div>
        <Label className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#3f3f46] mb-2 block">Hero Background Video</Label>
        <Input
          data-testid="settings-hero-video-input"
          value={settings.hero_video_url}
          onChange={(e) => setSettings({ ...settings, hero_video_url: e.target.value })}
          placeholder="URL or upload a .mp4 video"
          className="bg-[#050505] border-[#1A1A1A] text-white placeholder:text-[#3f3f46] rounded-none"
        />
        <FileUploader
          accept="video/mp4,video/webm,video/mov"
          label="Upload Hero Video"
          token={token}
          onUploaded={(url) => setSettings({ ...settings, hero_video_url: url })}
        />
        <p className="text-[10px] text-[#3f3f46] mt-1">Direct .mp4 video file. Leave empty to use the fallback image.</p>
      </div>

      <div>
        <Label className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#3f3f46] mb-2 block">Hero Fallback Image</Label>
        <Input
          data-testid="settings-hero-image-input"
          value={settings.hero_image_url}
          onChange={(e) => setSettings({ ...settings, hero_image_url: e.target.value })}
          placeholder="URL or upload an image"
          className="bg-[#050505] border-[#1A1A1A] text-white placeholder:text-[#3f3f46] rounded-none"
        />
        <FileUploader
          accept="image/*"
          label="Upload Fallback Image"
          token={token}
          onUploaded={(url) => setSettings({ ...settings, hero_image_url: url })}
        />
        <p className="text-[10px] text-[#3f3f46] mt-1">Used as poster/fallback if video is loading or empty.</p>
      </div>

      <div>
        <Label className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#3f3f46] mb-2 block">Hero Subtitle</Label>
        <Textarea
          data-testid="settings-hero-subtitle-input"
          value={settings.hero_subtitle}
          onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
          placeholder="Tagline displayed under the logo"
          rows={2}
          className="bg-[#050505] border-[#1A1A1A] text-white placeholder:text-[#3f3f46] rounded-none resize-none"
        />
      </div>

      {settings.hero_video_url && (
        <div>
          <p className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#3f3f46] mb-2">Preview</p>
          <div className="aspect-video bg-black border border-[#1A1A1A] overflow-hidden relative">
            <video src={settings.hero_video_url} autoPlay muted loop playsInline className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      <Button
        onClick={handleSave}
        disabled={saving}
        data-testid="settings-save-btn"
        className="bg-white text-[#050505] hover:bg-[#38BDF8] font-bold uppercase tracking-[0.2em] text-[11px] rounded-none"
      >
        {saving ? "Saving..." : "Save Settings"}
      </Button>
    </div>
  );
}

function ThoughtForm({ thought, onSave, onCancel, token }) {
  const [form, setForm] = useState(
    thought || {
      title: "",
      excerpt: "",
      content: "",
      category: "AI & Technology",
      author: "Mist Media",
      published_date: new Date().toISOString().split("T")[0],
      featured: false,
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) {
      toast.error("Title is required");
      return;
    }
    try {
      const headers = { "X-Admin-Token": token };
      if (thought) {
        await axios.put(`${API}/admin/thoughts/${thought.id}`, form, { headers });
        toast.success("Post updated!");
      } else {
        await axios.post(`${API}/admin/thoughts`, form, { headers });
        toast.success("Post created!");
      }
      onSave();
    } catch (err) {
      toast.error("Failed to save post");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#A1A1AA] mb-2 block">Title *</Label>
        <Input
          data-testid="thought-title-input"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Post title"
          className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] rounded-sm"
        />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#A1A1AA] mb-2 block">Excerpt</Label>
        <Input
          data-testid="thought-excerpt-input"
          value={form.excerpt}
          onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
          placeholder="Short teaser (1-2 sentences)"
          className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] rounded-sm"
        />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#A1A1AA] mb-2 block">Content</Label>
        <Textarea
          data-testid="thought-content-input"
          value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          placeholder="Full article text. Use double line breaks for paragraphs."
          rows={10}
          className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] rounded-sm resize-none"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#A1A1AA] mb-2 block">Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="thought-category-select" className="bg-[#0A0A0A] border-[#27272A] text-white rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-[#27272A]">
              <SelectItem value="AI & Technology">AI & Technology</SelectItem>
              <SelectItem value="Industry Insights">Industry Insights</SelectItem>
              <SelectItem value="Streaming & Distribution">Streaming & Distribution</SelectItem>
              <SelectItem value="Indie Filmmaking">Indie Filmmaking</SelectItem>
              <SelectItem value="Creative Process">Creative Process</SelectItem>
              <SelectItem value="Culture & Trends">Culture & Trends</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#A1A1AA] mb-2 block">Published Date</Label>
          <Input
            data-testid="thought-date-input"
            type="date"
            value={form.published_date}
            onChange={(e) => setForm({ ...form, published_date: e.target.value })}
            className="bg-[#0A0A0A] border-[#27272A] text-white rounded-sm"
          />
        </div>
        <div className="flex items-end pb-1">
          <div className="flex items-center gap-3">
            <Switch
              data-testid="thought-featured-switch"
              checked={form.featured}
              onCheckedChange={(c) => setForm({ ...form, featured: c })}
            />
            <Label className="text-sm text-[#A1A1AA]">Featured</Label>
          </div>
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <Button
          type="submit"
          data-testid="thought-save-btn"
          className="bg-white text-[#050505] hover:bg-[#38BDF8] font-bold uppercase tracking-wider text-sm rounded-sm"
        >
          {thought ? "Update Post" : "Create Post"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#52525B] rounded-sm"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("mist_admin_token") || "");
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [thoughts, setThoughts] = useState([]);
  const [editProject, setEditProject] = useState(null);
  const [editThought, setEditThought] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showThoughtForm, setShowThoughtForm] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/projects`, {
        headers: { "X-Admin-Token": token },
      });
      setProjects(res.data);
    } catch {
      console.error("Failed to fetch projects");
    }
  }, [token]);

  const fetchContacts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts`, {
        headers: { "X-Admin-Token": token },
      });
      setContacts(res.data);
    } catch {
      console.error("Failed to fetch contacts");
    }
  }, [token]);

  const fetchThoughts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/thoughts`, {
        headers: { "X-Admin-Token": token },
      });
      setThoughts(res.data);
    } catch {
      console.error("Failed to fetch thoughts");
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchProjects();
      fetchContacts();
      fetchThoughts();
    }
  }, [token, fetchProjects, fetchContacts, fetchThoughts]);

  const handleLogin = (t) => {
    setToken(t);
    localStorage.setItem("mist_admin_token", t);
  };

  const handleLogout = () => {
    setToken("");
    localStorage.removeItem("mist_admin_token");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await axios.delete(`${API}/admin/projects/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Project deleted");
      fetchProjects();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleSave = () => {
    setShowForm(false);
    setEditProject(null);
    fetchProjects();
  };

  const handleDeleteThought = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/admin/thoughts/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Post deleted");
      fetchThoughts();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleSaveThought = () => {
    setShowThoughtForm(false);
    setEditThought(null);
    fetchThoughts();
  };

  if (!token) return <AdminLogin onLogin={handleLogin} />;

  return (
    <div data-testid="admin-page" className="min-h-screen pt-28 pb-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-['Syne'] text-3xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-sm text-[#A1A1AA] mt-1">Manage your projects and view messages.</p>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            variant="outline"
            className="border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-red-500/50 rounded-sm"
          >
            <LogOut size={14} className="mr-2" /> Logout
          </Button>
        </div>

        <Tabs defaultValue="projects" className="w-full">
          <TabsList className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-none mb-8">
            <TabsTrigger data-testid="admin-projects-tab" value="projects" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#050505]">
              Projects ({projects.length})
            </TabsTrigger>
            <TabsTrigger data-testid="admin-contacts-tab" value="contacts" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#050505]">
              Messages ({contacts.length})
            </TabsTrigger>
            <TabsTrigger data-testid="admin-settings-tab" value="settings" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#050505]">
              <Settings size={12} className="mr-1.5" /> Settings
            </TabsTrigger>
            <TabsTrigger data-testid="admin-thoughts-tab" value="thoughts" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#050505]">
              <FileText size={12} className="mr-1.5" /> Thoughts ({thoughts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects">
            {!showForm ? (
              <>
                <Button
                  onClick={() => { setEditProject(null); setShowForm(true); }}
                  data-testid="admin-add-project-btn"
                  className="mb-6 bg-white text-[#050505] hover:bg-[#38BDF8] font-bold uppercase tracking-wider text-sm rounded-sm"
                >
                  <Plus size={14} className="mr-2" /> New Project
                </Button>

                <div className="space-y-2">
                  {projects.map((p) => (
                    <div
                      key={p.id}
                      data-testid={`admin-project-row-${p.id}`}
                      className="admin-table-row flex items-center gap-4 p-4 border border-[#1A1A1A] rounded-sm"
                    >
                      {p.poster_image && (
                        <img src={p.poster_image} alt="" className="w-12 h-16 object-cover rounded-sm flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white truncate">{p.title}</p>
                        <p className="text-xs text-[#A1A1AA]">{p.genre} &middot; {p.status}</p>
                      </div>
                      {p.featured && <Badge variant="outline" className="border-[#38BDF8] text-[#38BDF8] text-[10px]">Featured</Badge>}
                      <div className="flex gap-2 flex-shrink-0">
                        <a href={`/projects/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 text-[#A1A1AA] hover:text-white transition-colors">
                          <Eye size={14} />
                        </a>
                        <button
                          onClick={() => { setEditProject(p); setShowForm(true); }}
                          data-testid={`admin-edit-btn-${p.id}`}
                          className="p-2 text-[#A1A1AA] hover:text-[#38BDF8] transition-colors"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          data-testid={`admin-delete-btn-${p.id}`}
                          className="p-2 text-[#A1A1AA] hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {projects.length === 0 && (
                    <p className="text-center py-12 text-[#A1A1AA]">No projects yet. Create one!</p>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6">
                <h2 className="font-['Syne'] text-xl font-bold text-white mb-6">
                  {editProject ? "Edit Project" : "New Project"}
                </h2>
                <ProjectForm
                  project={editProject}
                  onSave={handleSave}
                  onCancel={() => { setShowForm(false); setEditProject(null); }}
                  token={token}
                />
              </div>
            )}
          </TabsContent>

          <TabsContent value="contacts">
            <div className="space-y-2">
              {contacts.map((c) => (
                <div key={c.id} className="p-4 border border-[#1A1A1A] rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold text-white">{c.name}</p>
                    <p className="text-xs text-[#52525B]">{new Date(c.created_at).toLocaleDateString()}</p>
                  </div>
                  <p className="text-xs text-[#A1A1AA] mb-1">{c.email} {c.subject && `- ${c.subject}`}</p>
                  <p className="text-sm text-[#D4D4D8]">{c.message}</p>
                </div>
              ))}
              {contacts.length === 0 && (
                <div className="text-center py-12">
                  <Inbox size={32} className="mx-auto text-[#3f3f46] mb-3" />
                  <p className="text-[#A1A1AA]">No messages yet.</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="settings">
            <SiteSettings token={token} />
          </TabsContent>

          <TabsContent value="thoughts">
            {!showThoughtForm ? (
              <>
                <Button
                  onClick={() => { setEditThought(null); setShowThoughtForm(true); }}
                  data-testid="admin-add-thought-btn"
                  className="mb-6 bg-white text-[#050505] hover:bg-[#38BDF8] font-bold uppercase tracking-wider text-sm rounded-sm"
                >
                  <Plus size={14} className="mr-2" /> New Post
                </Button>
                <div className="space-y-2">
                  {thoughts.map((t) => (
                    <div
                      key={t.id}
                      data-testid={`admin-thought-row-${t.id}`}
                      className="admin-table-row flex items-center gap-4 p-4 border border-[#1A1A1A] rounded-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white truncate">{t.title}</p>
                        <p className="text-xs text-[#A1A1AA]">{t.category} &middot; {t.published_date}</p>
                      </div>
                      {t.featured && <Badge variant="outline" className="border-[#38BDF8] text-[#38BDF8] text-[10px]">Featured</Badge>}
                      <div className="flex gap-2 flex-shrink-0">
                        <a href={`/thoughts/${t.slug}`} target="_blank" rel="noreferrer" className="p-2 text-[#A1A1AA] hover:text-white transition-colors">
                          <Eye size={14} />
                        </a>
                        <button
                          onClick={() => { setEditThought(t); setShowThoughtForm(true); }}
                          data-testid={`admin-edit-thought-${t.id}`}
                          className="p-2 text-[#A1A1AA] hover:text-[#38BDF8] transition-colors"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteThought(t.id)}
                          data-testid={`admin-delete-thought-${t.id}`}
                          className="p-2 text-[#A1A1AA] hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {thoughts.length === 0 && (
                    <p className="text-center py-12 text-[#A1A1AA]">No posts yet. Create one!</p>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6">
                <h2 className="font-['Syne'] text-xl font-bold text-white mb-6">
                  {editThought ? "Edit Post" : "New Post"}
                </h2>
                <ThoughtForm
                  thought={editThought}
                  onSave={handleSaveThought}
                  onCancel={() => { setShowThoughtForm(false); setEditThought(null); }}
                  token={token}
                />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
